/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { whitelabelTextsEn } from 'config/texts';
import { APP_NAME } from 'settings';
import {
  administrationDashboardPageEn,
  administrationOffersPageEn,
  administrationOrganizationsPageEn,
  administrationPageEn,
  administrationUsersPageEn
} from './administrationPage';
import { chooseOrganizationPageEn } from './chooseOrganizationPage';
import { invitationsPageEn } from './invitationsPage';
import { draftPageEn } from './onboarding/draftPage';
import { requestsPageEn } from './onboarding/requestsPage';
import { organizationsPageEn } from './organizationsPage';
import { profilePageEn } from './profilePage';
import { toastsEn } from './toasts';

export const en = {
  appName: APP_NAME,
  profile: 'Profile',
  organizations: 'Organizations',
  requests: 'Requests',
  solutions: 'Solutions',
  invitations: 'Invitations',
  legal: 'Legal',
  email: 'Email',
  solutionBar: {
    backToSolutionWithName: 'Back to "{{solutionName}}"',
    backToSolutionWithoutName: 'Back to solution'
  },
  navigation: {
    start: 'Home',
    subdomains: {
      book: 'Book',
      rent: 'Rent',
      profile: 'Profile',
      about: 'About',
      administration: 'Administration'
    },
    titles: {
      start: 'Navigate to home',
      book: 'Navigate to booking',
      rent: 'Navigate to rental',
      about: 'Navigate to about page',
      administration: 'Navigate to administration'
    }
  },
  navBar: {
    logout: 'Log out',
    bookings: 'Bookings',
    bookingRequests: 'Booking requests'
  },
  error: {
    title: 'Oops, something went wrong!',
    noResponse: 'The server is not responding.\nPlease try again later.',
    options:
      'We are looking into the problem immediately. If you need help or have questions, please contact the <0>{{appName}} Support Team</0>!',
    toHome: 'To home',
    toLandingPage: 'To {{appName}}',
    toOrganizations: 'To organizations'
  },
  form: {
    apply: 'Apply',
    mandatoryField: '*Required field',
    counter: '{{count}} / {{maxCount}} characters',
    maxCharLengthError:
      'Maximum character length exceeded. Please shorten the text.'
  },
  accessibility: {
    appURLLabel: 'Back to profile page',
    navigateToLabel: 'Navigate to URL:',
    navigateBackLabel: 'Back to previous page',
    fallbackImageAlt: 'Placeholder image (original could not be loaded)',
    profilePictureAlt: 'Profile picture',
    accordion: {
      deleteButtonLabel: 'Delete button',
      dragButtonLabel: 'Drag button'
    },
    menuToggleLabel: 'Options'
  },
  footer: {
    language: {
      title: 'Language'
    },
    links: {
      title: 'Useful Links',
      privacyPolicy: 'Privacy Policy',
      termsOfUse: 'Terms of Use',
      imprint: 'Imprint',
      accessibility: 'Accessibility',
      easyLanguage: 'Easy-to-read language'
    },
    help: {
      title: 'Questions & Help',
      contactUs: 'Simply use the contact form.',
      lookingForward: "We're looking forward to it!",
      contact: 'Contact',
      support: 'Support',
      faq: 'FAQ'
    },
    project: whitelabelTextsEn.footer.fundingNotice,
    bmleh: 'Funding notice logo'
  },
  toasts: toastsEn,
  profilePage: profilePageEn,
  organizationsPage: organizationsPageEn,
  chooseOrganizationPage: chooseOrganizationPageEn,
  invitationsPage: invitationsPageEn,
  draftPage: draftPageEn,
  requestsPage: requestsPageEn,
  administrationPage: administrationPageEn,
  administrationDashboardPage: administrationDashboardPageEn,
  administrationUsersPage: administrationUsersPageEn,
  administrationOrganizationsPage: administrationOrganizationsPageEn,
  administrationOffersPage: administrationOffersPageEn
};
